/** FLD-OFF-FIX-3 §3 — image work off the main thread.
 *
 *  Queueing a photo used to decode and re-draw it on the main thread (`createImageBitmap` + a
 *  canvas), which is a multi-hundred-millisecond stall on a phone photo and exactly the kind of
 *  block that used to make in-flight requests time out and the app call itself offline.
 *  It now runs in a Web Worker with an OffscreenCanvas; browsers without either fall back to the
 *  old main-thread path, which is still correct, just not free.
 */
const SRC = `
self.onmessage = async (e) => {
  const { id, file, max, quality } = e.data || {};
  try {
    const bmp = await createImageBitmap(file);
    const s = Math.min(1, max / Math.max(bmp.width, bmp.height));
    const c = new OffscreenCanvas(Math.round(bmp.width * s), Math.round(bmp.height * s));
    c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
    bmp.close();
    const blob = await c.convertToBlob({ type: "image/jpeg", quality: quality });
    self.postMessage({ id, blob });
  } catch (err) {
    self.postMessage({ id, error: String(err && err.message || err) });
  }
};
`;

let worker = null;
let broken = typeof Worker === "undefined" || typeof OffscreenCanvas === "undefined";
const waiting = new Map();
function get() {
  if (worker || broken) return worker;
  try {
    worker = new Worker(URL.createObjectURL(new Blob([SRC], { type: "text/javascript" })));
    worker.onmessage = (e) => { const w = waiting.get(e.data?.id); if (w) { waiting.delete(e.data.id); w(e.data); } };
    worker.onerror = () => { broken = true; worker = null; };
  } catch { broken = true; }
  return worker;
}
export const shrinkRunsInWorker = () => !!get();

/** A small JPEG of `file`, never bigger than `max` on its long edge. Returns `file` untouched if
 *  neither path can produce one — a thumbnail is a convenience, never a gate. */
export async function shrink(file, max = 320, q = 0.7) {
  const w = get();
  if (w) {
    const id = Math.random().toString(36).slice(2);
    const out = await new Promise((resolve) => {
      waiting.set(id, resolve);
      setTimeout(() => { if (waiting.delete(id)) resolve({ error: "timeout" }); }, 8000);
      w.postMessage({ id, file, max, quality: q });
    });
    if (out?.blob) return out.blob;
  }
  try {
    const bmp = await createImageBitmap(file);
    const s = Math.min(1, max / Math.max(bmp.width, bmp.height));
    const c = document.createElement("canvas");
    c.width = Math.round(bmp.width * s); c.height = Math.round(bmp.height * s);
    c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
    return await new Promise((r) => c.toBlob(r, "image/jpeg", q));
  } catch { return file; }
}
